import React, { useContext } from 'react';
import { useOverlayTriggerState } from '@react-stately/overlays';
import { Button, cn, DialogModal, IconClose, Typography, useModalDialogContext } from '@up-riac/bespoke-ui';
import RIACGoldLogo from '@riac/core/assets/riac_logo_transparent_gold_full.png?h=104';
import GunPhoto from 'images/riaclive-nag-popup/gun_photo_11_21_2025.jpg?w=358';
import { GlobalDataContext } from 'src/lib/GlobalDataContext';
import { useLocation } from 'react-router-dom';
import dayjs from 'dayjs';
import { AnalyticsContext } from 'src/lib/AnalyticsContext';

const RIACLiveNagPopupTitle = ({ id, children, className }) => {
  const { titleProps } = useModalDialogContext();
  return <Typography
    weight='extrabold'
    className={cn('text-[26px] leading-[32px] text-text-contrast', className)}
    {...titleProps}
    id={id}
  >
    {children}
  </Typography>;
};

export const RIACLiveNagPopup = () => {
  const location = useLocation();
  const { sendHotjarEvent } = useContext(AnalyticsContext);
  const { activeAuctions } = useContext(GlobalDataContext);
  const now = dayjs();
  const liveAuction = activeAuctions.find((a) =>
    a.active
    && a.AuctionTypeID != TIMED_AUCTION_TYPE_ID
    && (dayjs.tz(a.StartDate, 'America/Chicago').isSameOrBefore(now, 'day')
    && now.isSameOrBefore(dayjs.tz(a.EndDate, 'America/Chicago'), 'day'))
  );

  // Check if this auction was already dismissed
  const shouldShow = liveAuction && (() => {
    try {
      const dismissedAuctionId = localStorage.getItem('most_recent_dismissed_riaclive_nag_popup');
      return dismissedAuctionId !== liveAuction.AuctionID?.toString();
    } catch {
      return false; // No local storage, no nag.
    }
  })();

  const state = useOverlayTriggerState({ defaultOpen: !!shouldShow });

  const handleClose = (): void => {
    if(liveAuction?.AuctionID) {
      try {
        localStorage.setItem('most_recent_dismissed_riaclive_nag_popup', liveAuction.AuctionID.toString());
      } catch (error) {
        console.warn('Failed to save dismissed auction ID to localStorage:', error);
      }
    }
    state.close();
  };

  if(!liveAuction) return null;
  if(location.pathname !== '/') return null;

  return <DialogModal
    isOpen={state.isOpen}
    onClose={handleClose}
    isDismissable
  >
    <div className='dark grid grid-cols-1 lg:grid-cols-2 w-screen max-w-full lg:max-w-[716px] bg-surface-dark relative'>
      <div className='hidden lg:block'>
        <img src={GunPhoto} alt='Gun Photo' className={cn('w-full h-full object-cover')}/>
      </div>
      <div className='flex flex-col items-center px-5 py-10'>
        <img src={RIACGoldLogo} alt='Rock Island Auction Company' id='riac-live-nag-popup-logo' className={cn('h-[104px] mb-8')}/>
        <RIACLiveNagPopupTitle className='mb-6' id='riac-live-nag-popup-title'>Auction is Live!</RIACLiveNagPopupTitle>
        <Typography
          expressive heading weight='semibold' color='text-accent' className='font-expressive-heading mb-1'
          align='center'
          id='riac-live-nag-popup-auction-title'
        >
          {liveAuction.Title}
        </Typography>
        <Typography weight='medium' color='text-lighter' size='sm' align='center' className='mb-6'>
          It's the first auction of the year. 2026's Big Kickoff! Click now to watch and bid.
        </Typography>
        <Button variant='accent' size='lg' fullWidth to='/live' onPress={() => {
          sendHotjarEvent('UX-enter-live-bidding-modal-button-pressed');
          handleClose();
        }} id='riac-live-nag-popup-enter-live-bidding-button'>
          Enter Live Bidding
        </Button>
      </div>
      <div className='absolute top-3 right-3'>
        <Button onPress={handleClose} aria-label='Close' unstyled
          className='rounded-full size-9 text-text flex items-center justify-center'
          id='riac-live-nag-popup-close-button'
        >
          <IconClose/>
        </Button>
      </div>
    </div>
  </DialogModal>;
};
